import React, { useEffect, useState } from 'react'
import { Redirect } from 'react-router'
import { useDispatch } from 'react-redux'
import flowRight from 'lodash/flowRight'
import get from 'lodash/get'

import { Loading } from '@fielded/shared-ui'
import { SHIPMENT_STATUS } from '@fielded/fs-api/lib/shipment/constants'

import { withApi } from '../../../../../../common/ApiProvider'
import { withUser } from '../../../../../../common/AuthenticationProvider'
import withConfig from '../../../../../../van-shared/hoc/withConfig'
import withShipment from '../../../../common/WithShipmentWrapper'
import { isPsmResellerWarehouseUser, isPsmDriverUser } from '../../../../common/utils'
import { onGetGeolocation, useGeolocation } from '../../../../../../van-shared/hooks/useGeolocation'
import { retryGeolocation } from '../../../../../../utils/geolocation'
import { getPermission, PERMISSIONS, PERMISSION_STATES } from '../../../../../../utils/permissions-service'
import { onClearGeolocation } from '../../../../../../root/reducers/shipments/geolocation/geolocationSlice'
import { useAmplitudeAnalytics } from '../../../../../../common/analytics-tracking/AmplitudeAnalyticsProvider'
import PSMDeliveryLocation from './PSMDeliveryLocation'
import { hasFeature } from '../../../../../../van-shared/utils/features'

const PSMDeliveryLocationContainer = ({
  api,
  user,
  config,
  match,
  history,
  shipment
}) => {
  const dispatch = useDispatch()

  const {
    distance,
    geolocationData,
    loading,
    locationObject,
    hasValidFacilityCoordinates
  } = useGeolocation(api, shipment)

  const trackingHandler = useAmplitudeAnalytics()

  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)
  const [isRequestingPermission, setIsRequestingPermission] = useState(false)
  const [isRetryingGps, setIsRetryingGps] = useState(false)

  const {
    geoPosition,
    position,
    newPermission,
    positionErrorMsg,
    watchId
  } = geolocationData

  const isPsmResellerWarehouse = isPsmResellerWarehouseUser(config, user)
  const isPsmDriver = isPsmDriverUser(user)
  const isSkipDriverDeliveryOtp = hasFeature(config, 'features:shipments:skipDriverDeliveryOtp')
  const otpKey = get(shipment, 'otp.key', { version: 'v1' })
  const snapshotId = get(shipment, 'snapshotId')

  const isShipmentReceived = shipment.status === SHIPMENT_STATUS.RECEIVED

  const skipGPSValidation = isPsmResellerWarehouse || get(shipment, 'isGpsAuthenticated', false) || !!(get(shipment, 'gpsValidationFailComment.reason', false))
  const skipOTPValidation = (isPsmDriver && isSkipDriverDeliveryOtp) || get(shipment, 'otp.hasKeyAndPassword', false) || get(shipment, 'otp.userFailReason', '')
  const skipGPSAndOTPValidation = !isShipmentReceived && skipGPSValidation && skipOTPValidation

  const onOpenBottomDrawer = () => {
    setIsBottomDrawerOpen(true)
  }

  const onCloseBottomDrawer = () => {
    setIsBottomDrawerOpen(false)
  }

  const onOpenPermissionModal = () => {
    setIsPermissionModalOpen(true)
  }

  const onClosePermissionModal = () => {
    setIsRequestingPermission(false)
    setIsPermissionModalOpen(false)
  }

  const onRequestLocationPermission = async () => {
    setIsRequestingPermission(true)

    const callback = () => {
      onOpenBottomDrawer()
      onClosePermissionModal()
    }

    await onGetGeolocation({
      permission: newPermission,
      dispatch,
      watchId,
      callback
    })
  }

  const onRetryGeolocation = async () => {
    setIsRetryingGps(true)
    try {
      // Reset our cached permission/position state first. If the user reset
      // the site permission in browser settings, redux still says DENIED
      // and any retry that branches on `permission` would short-circuit.
      // Clearing wipes positionErrorMsg + position so the next attempt
      // starts from a clean PROMPT-like state.
      dispatch(onClearGeolocation(watchId))

      // Re-query the live browser permission state.
      const currentState = await getPermission(PERMISSIONS.GEOLOCATION)
      if (currentState === PERMISSION_STATES.DENIED) {
        // Browser still has it denied - getCurrentPosition will reject
        // synchronously and surface the denial message. The user needs
        // to reset permission in browser settings.
        await retryGeolocation(dispatch)
        return
      }
      // prompt/granted - start a fresh fix (will re-trigger the prompt if needed).
      await onGetGeolocation({
        permission: currentState || PERMISSION_STATES.PROMPT,
        dispatch,
        watchId: null
      })
    } catch (error) {
      console.warn('[GPS] Retry failed:', error)
    } finally {
      setIsRetryingGps(false)
    }
  }

  const initialize = () => {
    trackingHandler.shipment.viewed({ shipment })
  }

  useEffect(() => {
    initialize()
  }, [])

  if (skipGPSAndOTPValidation) {
    return <Redirect to={`/shipments/pick-list/${snapshotId}`} />
  }

  if (loading || !locationObject) {
    return <Loading loadingText='Loading facility details...' />
  }

  return (
    <PSMDeliveryLocation
      api={api}
      otpKey={otpKey}
      skipGPSValidation={skipGPSValidation}
      skipOTPValidation={skipOTPValidation}
      history={history}
      watchId={watchId}
      shipment={shipment}
      snapshotId={snapshotId}
      permission={newPermission}
      geoPosition={geoPosition}
      position={position}
      distance={distance}
      locationObject={locationObject}
      positionErrorMsg={positionErrorMsg}
      isBottomDrawerOpen={isBottomDrawerOpen}
      isPermissionModalOpen={isPermissionModalOpen}
      isRequestingPermission={isRequestingPermission}
      isPsmResellerWarehouse={isPsmResellerWarehouse}
      onOpenBottomDrawer={onOpenBottomDrawer}
      onCloseBottomDrawer={onCloseBottomDrawer}
      onOpenPermissionModal={onOpenPermissionModal}
      onClosePermissionModal={onClosePermissionModal}
      onRequestLocationPermission={onRequestLocationPermission}
      hasValidFacilityCoordinates={hasValidFacilityCoordinates}
      onRetryGeolocation={onRetryGeolocation}
      isRetryingGps={isRetryingGps}
      trackingHandler={trackingHandler}
    />
  )
}

const withHOCs = flowRight(
  withApi,
  withUser,
  withConfig,
  withShipment
)

export default withHOCs(PSMDeliveryLocationContainer)
